import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navigate } from 'react-router-dom';
import { Lock, User, Archive } from 'lucide-react';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-pulse text-zinc-400 font-body">Loading...</div>
      </div>
    );
  }
  if (user) return <Navigate to="/" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === 'string') setError(d);
      else if (Array.isArray(d)) setError(d.map(x => x.msg || '').join(' '));
      else setError('Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-zinc-950 overflow-hidden">
        <img
          src="https://images.pexels.com/photos/1597623/pexels-photo-1597623.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-12">
          <Archive className="w-14 h-14 mb-5 opacity-90" />
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight font-heading">
            Archive Extractor
          </h1>
          <p className="mt-4 text-base text-zinc-300 text-center max-w-md font-body">
            Download clean copies of archived websites from the Wayback Machine. No overlay, no scripts — just the original site.
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAFA]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <Archive className="w-7 h-7 text-zinc-950" />
            <span className="text-xl font-black tracking-tight font-heading text-zinc-950">Archive Extractor</span>
          </div>

          <h2 className="text-xl font-bold tracking-tight font-heading text-zinc-950">Sign in</h2>
          <p className="mt-1 text-sm text-zinc-500 font-body">Enter your credentials to continue</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="login-form">
            {error && (
              <div className="p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="login" className="text-zinc-700 font-medium text-sm">Login</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="login"
                  type="text"
                  placeholder="Your login"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 border-zinc-300 focus:ring-2 focus:ring-blue-600 rounded-sm"
                  data-testid="login-input"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-700 font-medium text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 border-zinc-300 focus:ring-2 focus:ring-blue-600 rounded-sm"
                  data-testid="password-input"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-zinc-950 hover:bg-zinc-800 text-white rounded-sm h-10"
              disabled={submitting}
              data-testid="login-submit-button"
            >
              {submitting ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
